import React from 'react';
import { AppView, Article } from '../types';
import { ArrowRight, BookOpen, Lock, Clock } from 'lucide-react';

interface GuidesHubProps {
  setView: (view: AppView) => void;
}

const ARTICLES: Article[] = [
  {
    id: 'oci-aadhaar-guide',
    title: 'Demystifying OCI Passport Updates & Aadhaar Rules for Your Family',
    category: 'Documents & Visas',
    categoryColor: 'indigo',
    excerpt: 'The 90-day APIS deadline, the Age-20 reissue rule, and how long OCI children must wait before applying for Aadhaar. The definitive guide to navigating the OCI and Aadhaar landscape.',
    readTimeMinutes: 8,
    publishedDate: '2026-01-15',
    heroImage: '/images/nri-guide-hero.png',
    appView: AppView.NRI_GUIDE,
    comingSoon: false,
  },
  {
    id: 'rnor-tax-window',
    title: 'The RNOR Tax Window: Your One-Time Opportunity to Sell US Stocks Tax-Free',
    category: 'Finance & Tax',
    categoryColor: 'emerald',
    excerpt: 'Most returning NRIs leave lakhs on the table by not acting during the 2-year RNOR window. Covers 401k strategy, US stock sales, the US home decision, and Form 10EE.',
    readTimeMinutes: 10,
    publishedDate: '2026-02-01',
    heroImage: null,
    heroVisual: 'rnor',
    appView: AppView.RNOR_GUIDE,
    comingSoon: false,
  },
  {
    id: 'school-admission-timeline',
    title: 'The IB/IGCSE School Admission Guide: Boards, Calendars & Mid-Year Realities',
    category: 'Schools & Education',
    categoryColor: 'amber',
    excerpt: 'Pune schools begin admissions as early as November for the following June. Covers IB vs CBSE, the admission calendar, mid-year schools, and area-wise recommendations.',
    readTimeMinutes: 7,
    publishedDate: '2026-02-15',
    heroImage: null,
    heroVisual: 'school',
    appView: AppView.SCHOOL_GUIDE,
    comingSoon: false,
  },
  {
    id: 'pune-rental-guide',
    title: 'Renting in Pune as a Returning NRI: The Leave & Licence Agreement Explained',
    category: 'Housing',
    categoryColor: 'rose',
    excerpt: "Maharashtra's Leave & Licence model is not a standard tenancy lease. What every returnee must know before signing anything — registration, rent escalation, and security deposits.",
    readTimeMinutes: 6,
    publishedDate: '',
    heroImage: null,
    heroVisual: 'rental',
    appView: null,
    comingSoon: true,
  },
];

const BADGE_CLASSES: Record<string, { bg: string; text: string; icon: string; gradient: string; border: string }> = {
  indigo:  { bg: 'bg-indigo-100',  text: 'text-indigo-700',  icon: 'text-indigo-400',  gradient: 'from-indigo-600 to-indigo-400',  border: 'border-indigo-200'  },
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: 'text-emerald-400', gradient: 'from-emerald-600 to-teal-400',   border: 'border-emerald-200' },
  amber:   { bg: 'bg-amber-100',   text: 'text-amber-700',   icon: 'text-amber-400',   gradient: 'from-amber-600 to-orange-400',   border: 'border-amber-200'   },
  rose:    { bg: 'bg-rose-100',    text: 'text-rose-700',    icon: 'text-rose-400',    gradient: 'from-rose-600 to-pink-400',      border: 'border-rose-200'    },
};

function HeroVisual({ visual, colorClass }: { visual: Article['heroVisual']; colorClass: typeof BADGE_CLASSES[string] }) {
  if (visual === 'rnor') {
    return (
      <div className={`w-full h-full bg-gradient-to-br from-slate-800 to-emerald-900 flex items-center justify-center relative overflow-hidden`}>
        <div className="absolute inset-0 opacity-10">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="absolute bottom-0 bg-emerald-400 rounded-t-sm"
              style={{ left: `${10 + i * 14}%`, width: '8%', height: `${30 + i * 10}%`, opacity: 0.6 + i * 0.05 }} />
          ))}
        </div>
        <div className="relative z-10 flex items-end gap-3 px-6">
          <div className="text-center">
            <div className="text-2xl font-black text-white/90">$</div>
            <div className="text-[10px] text-emerald-300 font-bold uppercase tracking-wider mt-1">US Stocks</div>
          </div>
          <div className="flex flex-col items-center gap-1 pb-1">
            <div className="flex gap-0.5">
              {[1,2,3].map(i => <div key={i} className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" style={{ animationDelay: `${i * 200}ms` }} />)}
            </div>
            <ArrowRight size={16} className="text-emerald-400" />
            <div className="text-[10px] text-emerald-300 font-bold uppercase tracking-wider">RNOR</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-black text-white/90">₹</div>
            <div className="text-[10px] text-emerald-300 font-bold uppercase tracking-wider mt-1">Tax-Free</div>
          </div>
        </div>
        <div className="absolute bottom-3 right-4 text-[10px] text-emerald-400/60 font-semibold uppercase tracking-widest">Finance & Tax</div>
      </div>
    );
  }

  if (visual === 'school') {
    return (
      <div className="w-full h-full bg-gradient-to-br from-slate-800 to-amber-900 flex items-center justify-center relative overflow-hidden">
        <div className="relative z-10 flex flex-col items-center gap-3 px-6 w-full">
          <div className="flex gap-3 w-full justify-center">
            {[
              { label: 'IB', sub: 'International', color: 'bg-amber-500' },
              { label: 'IGCSE', sub: 'Cambridge', color: 'bg-orange-500' },
              { label: 'CBSE', sub: 'National', color: 'bg-yellow-600' },
            ].map(b => (
              <div key={b.label} className={`${b.color} rounded-lg px-3 py-2 text-center flex-1`}>
                <div className="text-white font-black text-sm">{b.label}</div>
                <div className="text-white/70 text-[9px] font-medium">{b.sub}</div>
              </div>
            ))}
          </div>
          <div className="text-[10px] text-amber-300 font-semibold uppercase tracking-widest">Choose the right board →</div>
        </div>
        <div className="absolute bottom-3 right-4 text-[10px] text-amber-400/60 font-semibold uppercase tracking-widest">Schools & Education</div>
      </div>
    );
  }

  if (visual === 'rental') {
    return (
      <div className="w-full h-full bg-gradient-to-br from-slate-800 to-rose-900 flex items-center justify-center relative overflow-hidden">
        <div className="relative z-10 flex flex-col items-center gap-2">
          <div className="bg-white/10 border border-white/20 rounded-2xl px-6 py-4 text-center backdrop-blur-sm">
            <div className="text-white font-black text-lg">Leave &</div>
            <div className="text-white font-black text-lg">Licence</div>
            <div className="text-rose-300 text-[10px] font-bold uppercase tracking-widest mt-1">Maharashtra Rental Law</div>
          </div>
        </div>
        <div className="absolute bottom-3 right-4 text-[10px] text-rose-400/60 font-semibold uppercase tracking-widest">Housing</div>
      </div>
    );
  }

  return (
    <div className={`w-full h-full bg-gradient-to-br ${colorClass.gradient} flex items-center justify-center`}>
      <BookOpen size={40} className="text-white/40" />
    </div>
  );
}

const GuidesHub: React.FC<GuidesHubProps> = ({ setView }) => {
  const live = ARTICLES.filter(a => !a.comingSoon);
  const coming = ARTICLES.filter(a => a.comingSoon);

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8 pb-24 md:pb-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-10 w-10 bg-indigo-100 rounded-xl flex items-center justify-center">
            <BookOpen size={20} className="text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-slate-800">Relocation Guides</h1>
            <p className="text-slate-500 text-sm">Expert-written deep dives for NRIs returning to India</p>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-4">
          <span className="text-xs font-bold px-2.5 py-1 bg-indigo-100 text-indigo-700 rounded-full">🇮🇳 All NRIs</span>
          <span className="text-xs text-slate-400">{live.length} guides published · {coming.length} coming soon</span>
        </div>
      </div>

      {/* Published articles */}
      <div className="space-y-4 mb-10">
        {live.map((article) => {
          const badge = BADGE_CLASSES[article.categoryColor] ?? BADGE_CLASSES.indigo;
          return (
            <div
              key={article.id}
              onClick={() => article.appView && setView(article.appView)}
              className="group bg-white rounded-2xl border border-slate-100 overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer flex"
            >
              {/* Thumbnail */}
              <div className="w-36 md:w-52 flex-shrink-0 h-auto min-h-[120px] relative overflow-hidden">
                {article.heroImage ? (
                  <img src={article.heroImage} alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <HeroVisual visual={article.heroVisual} colorClass={badge} />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 p-5 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${badge.bg} ${badge.text}`}>
                      {article.category}
                    </span>
                    <span className="text-[11px] text-slate-400 flex items-center gap-1">
                      <Clock size={10} /> {article.readTimeMinutes} min read
                    </span>
                  </div>
                  <h2 className="font-bold text-slate-800 text-base leading-snug mb-2 group-hover:text-indigo-700 transition-colors line-clamp-2">
                    {article.title}
                  </h2>
                  <p className="text-slate-500 text-xs leading-relaxed line-clamp-2 hidden sm:block">
                    {article.excerpt}
                  </p>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-1.5 text-indigo-600 font-bold text-xs group-hover:gap-2.5 transition-all duration-200">
                    Read Guide <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
                  </div>
                  {article.publishedDate && (
                    <span className="text-slate-300 text-[10px]">
                      {new Date(article.publishedDate).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Coming Soon */}
      {coming.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Coming Soon</span>
            <div className="flex-1 h-px bg-slate-100" />
          </div>
          <div className="space-y-3">
            {coming.map((article) => {
              const badge = BADGE_CLASSES[article.categoryColor] ?? BADGE_CLASSES.indigo;
              return (
                <div key={article.id}
                  className="bg-slate-50 rounded-xl border border-slate-100 overflow-hidden flex opacity-70">
                  <div className="w-24 flex-shrink-0 min-h-[80px] relative overflow-hidden">
                    <HeroVisual visual={article.heroVisual} colorClass={badge} />
                  </div>
                  <div className="flex-1 p-4 flex items-center justify-between gap-3">
                    <div>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${badge.bg} ${badge.text} mb-1.5 inline-block`}>
                        {article.category}
                      </span>
                      <p className="font-semibold text-slate-600 text-sm leading-snug line-clamp-1">{article.title}</p>
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-400 text-[11px] font-bold uppercase tracking-wide shrink-0">
                      <Lock size={11} /> Soon
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default GuidesHub;
